import { Model, Factory, belongsTo } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import {
  randSentence,
  randBoolean,
  randNumber,
  randRecentDate,
  randFutureDate,
  randPastDate,
  randProductName,
  randWord,
  rand,
  randChanceBoolean,
} from '@ngneat/falso'
import type { SchemaAttributes } from '../types.js'
import type { MockServer, MirageServer } from './server.js'
import { toNumber } from '@/utils/number.js'
import { filterable, pageable, sortable } from './utils.js'

type EstimateData = SchemaAttributes<'Estimate'>

export const model = (Model as ModelDefinition<EstimateData>).extend({
  consultation: belongsTo('consultation'),
})

export const factory = Factory.extend<EstimateData>({
  title() {
    return `${randProductName()} - ${randWord()}`
  },

  notes() {
    return randChanceBoolean({ chanceTrue: 0.7 })
      ? randSentence({ length: randNumber({ min: 1, max: 3 }) }).join(' ')
      : null
  },

  notes_pdf() {
    return randBoolean()
  },

  // Financial amounts (in cents, converted to euros)
  subtotal() {
    const amount = randNumber({ min: 10000, max: 500000 }) / 100 // €100 to €5000
    return Number(amount.toFixed(2))
  },

  vat() {
    const vatRate = rand([0.06, 0.12, 0.21]) // Common VAT rates
    const subtotal =
      toNumber(this.subtotal) || randNumber({ min: 100, max: 5000 })
    const vatAmount = subtotal * vatRate
    return toNumber(vatAmount.toFixed(2))
  },

  total() {
    const subtotal =
      toNumber(this.subtotal) || randNumber({ min: 100, max: 5000 })
    const vat = toNumber(this.vat) || toNumber(subtotal) * 0.21
    return Number((subtotal + vat).toFixed(2))
  },

  min_subtotal() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? Number((randNumber({ min: 5000, max: 20000 }) / 100).toFixed(2))
      : null
  },

  min_vat() {
    const minSubtotal = toNumber(this.min_subtotal)
    return minSubtotal ? Number((minSubtotal * 0.21).toFixed(2)) : null
  },

  min_total() {
    const minSubtotal = toNumber(this.min_subtotal)
    const minVat = toNumber(this.min_vat)
    return minSubtotal && minVat
      ? Number((minSubtotal + minVat).toFixed(2))
      : null
  },

  max_subtotal() {
    return randChanceBoolean({ chanceTrue: 0.3 })
      ? Number((randNumber({ min: 100000, max: 1000000 }) / 100).toFixed(2))
      : null
  },

  max_vat() {
    const maxSubtotal = toNumber(this.max_subtotal)
    return maxSubtotal ? Number((maxSubtotal * 0.21).toFixed(2)) : null
  },

  max_total() {
    const maxSubtotal = toNumber(this.max_subtotal)
    const maxVat = toNumber(this.max_vat)
    return maxSubtotal && maxVat
      ? Number((maxSubtotal + maxVat).toFixed(2))
      : null
  },

  // Status: 0 - Draft, 1 - Finalized
  status() {
    return rand([0, 1])
  },

  archived() {
    return randChanceBoolean({ chanceTrue: 0.1 }) // 10% chance of being archived
  },

  preference_time() {
    return randChanceBoolean({ chanceTrue: 0.4 })
      ? randFutureDate({ years: 1 }).toISOString()
      : null
  },

  created() {
    return randPastDate({ years: 2 }).toISOString()
  },

  modified() {
    return randRecentDate({ days: 30 }).toISOString()
  },

  vat_calculation_method() {
    return rand([1, 2, 3])
  },

  determining_price() {
    return rand([1, 2])
  },

  dispense_fee_method() {
    return rand([0, 1])
  },

  dispense_fee_calculation_method() {
    return rand([0, 1])
  },
  injection_fee_method() {
    return rand([0, 1])
  },

  injection_fee_type() {
    return rand([1, 2])
  },
})

export function routes(server: MockServer) {
  server.get('/estimates', function (this: MirageServer, schema, request) {
    const estimates = schema.all('estimate')

    // Apply filtering
    const filtered = filterable(estimates, request, {
      consultation: (item, value) => item.consultationId === value,
    })

    // Apply sorting
    const sorted = sortable(filtered, request, {}, ['-created'])

    // Apply pagination
    const paginated = pageable(sorted, request)

    // Format response as JSON:API
    const response = this.serialize(paginated.data)

    return {
      ...response,
      meta: {
        pagination: paginated.meta,
      },
    }
  })

  server.post('/estimates', function (schema, request) {
    const { data } = JSON.parse(request.requestBody)
    const estimate = schema.create('estimate', data.attributes)

    return estimate
  })

  // Handle linkable estimates endpoint
  server.get(
    '/consultations/:consultationId/estimates/linkable_estimates',
    function (this: MirageServer, schema, _request) {
      // Get estimates that are not linked to any consultation (consultationId is null)
      const unlinkableEstimates = schema.where('estimate', {
        consultationId: null,
      })

      // Format response as JSON:API
      return this.serialize(unlinkableEstimates)
    },
  )

  // Handle bulk link estimates endpoint
  server.post(
    '/consultations/:consultationId/estimates/bulk_link',
    function (schema, request) {
      const { data } = JSON.parse(request.requestBody)
      const consultationId = request.params.consultationId
      const estimateIds = data.attributes.estimate_ids

      // Update estimates to link them to the consultation
      estimateIds.forEach((estimateId: string) => {
        const estimate = schema.find('estimate', estimateId)
        if (estimate) {
          estimate.update({ consultationId })
        }
      })

      return { success: true }
    },
  )
}

export function seeds(server: MockServer) {
  // Get all existing consultations
  const consultations = server.schema.all('consultation').models

  // Create 1-2 estimates for each existing consultation
  consultations.forEach((consultation) => {
    const estimateCount = randNumber({ min: 1, max: 2 })
    for (let i = 0; i < estimateCount; i++) {
      server.create('estimate', {
        consultationId: consultation.id,
      })
    }
  })

  // Create a few estimates without consultation (unlinked)
  for (let i = 0; i < 3; i++) {
    server.create('estimate', {
      consultationId: null,
    })
  }
}
